import React, { FC, useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Radio, RadioChangeEvent } from 'antd';
import { useForm } from 'antd/es/form/Form';
import ReactInputMask, { Props as InputMaskProps } from 'react-input-mask';
import { Divider, SearchInput } from 'common/components';
import { DEFAULT_VALUE_0, PHONE_MASK, PHONE_PATTERN_WITH_CODE } from 'common/config';
import { Copy, Tick, History } from 'common/svg';
import { normalizePhone, phoneFormatter, renderRequiredMark, rules, validatePhone } from 'common/helpers';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { EDeliveryType, EPickUpDate, IBasket, IUpdateBasketPayload } from 'entities/Basket/Basket.models';
import {
  filterPickUpStores,
  formatBasketStoreSchedules,
  getBasketDeliveryType,
  getDefaultPickUpDateValue,
  getDeliveryDateByPickUpDate,
  getPickUpDateOptions,
  mapBasketGoodsToPayload,
  translatePickUpDate,
} from 'entities/Basket/Basket.helper';
import { UserAssigneeSelector } from 'entities/User/components';
import { getUserAssigneeOptions } from 'entities/User/User.helper';
import { selectCurrentUser, selectUserAssignees, selectUserList } from 'entities/User/User.slice';
import { getBasketById, getBasketStores, selectBasketStores } from 'entities/Basket/Basket.slice';
import { SelectCityModal } from 'entities/City/components';
import { CityHooks } from 'entities/City/City.hooks';

const InputMask = ReactInputMask as React.ComponentType<InputMaskProps>;

interface IComponentProps {
  basket: IBasket;
  onSubmit: (payload: Omit<IUpdateBasketPayload, 'id'>) => void;
}

export const PickUpMethod: FC<IComponentProps> = ({ basket, onSubmit }) => {
  const dispatch = useAppDispatch();
  // basket
  const basketStores = useAppSelector(selectBasketStores);
  // user
  const currentUser = useAppSelector(selectCurrentUser);
  const userList = useAppSelector(selectUserList);
  const userAssignees = useAppSelector(selectUserAssignees);

  const [payload, setPayload] = useState<Omit<IUpdateBasketPayload, 'id'>>({ deliveryType: EDeliveryType.PickUp });
  const [storeSearch, setStoreSearch] = useState<string>('');
  const [pickUpDate, setPickUpDate] = useState<EPickUpDate | null>(null);
  const [storeId, setStoreId] = useState<number | null>(null);
  const [form] = useForm();
  const phone = Form.useWatch('phone', form);
  const assignees = useMemo(() => getUserAssigneeOptions(userList, userAssignees, ''), [userList, userAssignees]);
  const {
    openSelectCityModal,
    props: { onSubmit: updateUserCity, ...selectCityModalProps },
  } = CityHooks.useSelectCityModal();

  const { isPickUp } = getBasketDeliveryType(basket);

  useEffect(() => {
    const defaultPickUpDateValue = getDefaultPickUpDateValue(basketStores);

    setPickUpDate(defaultPickUpDateValue);
    setPayload((prev) => ({
      ...prev,
      deliveryDate: getDeliveryDateByPickUpDate(defaultPickUpDateValue),
    }));

    if (isPickUp) {
      const assigneeExists = assignees.find((item) => item.label === basket.assignee);

      form.setFieldsValue({
        assignee: assigneeExists ? assigneeExists.value : undefined,
        phone: phoneFormatter(basket.phone),
      });

      const store = basketStores.find((item) => {
        const date = getDeliveryDateByPickUpDate(item.pickUpDate as EPickUpDate);

        if (basket.deliveryDate) {
          return item.id === basket.storeId && date === basket.deliveryDate;
        }

        return item.id === basket.storeId;
      });

      if (store) {
        setStoreId(store.id);
        setPickUpDate(store.pickUpDate as EPickUpDate);
      }
    }
  }, [basketStores]);

  const dateOptions = getPickUpDateOptions(basketStores);
  const availableStores = filterPickUpStores(basketStores, storeSearch, pickUpDate);
  const isValidPhone = validatePhone(phone);
  const selectedStore = basketStores.find((item) => item.id === storeId);

  const copyPhone = (e: React.MouseEvent): void => {
    e.stopPropagation();
    const phone = form.getFieldValue('phone');

    navigator.clipboard.writeText(`${phone}`);
  };

  const handleSearchStore = async (value?: string): Promise<void> => {
    setStoreSearch(value ?? '');
  };

  const changePickUpDate = (e: RadioChangeEvent): void => {
    const value = e.target.value as EPickUpDate;

    setPickUpDate(value);
    setPayload((prev) => ({
      ...prev,
      deliveryDate: getDeliveryDateByPickUpDate(value),
    }));
  };

  const changeStore = (id: number): void => {
    setStoreId(id);
    setPayload((prev) => ({ ...prev, storeId: id }));
  };

  const changeForm = (values: Omit<IUpdateBasketPayload, 'id'>): void => {
    setPayload((prev) => ({ ...prev, ...values }));
  };

  const handleUserCityChange = async (cityId: number, onSuccess: () => void): Promise<void> => {
    await dispatch(getBasketStores({ cityId, goods: mapBasketGoodsToPayload(basket.goods) }));
    await updateUserCity(cityId, onSuccess);
    // Бэкенд сбрасывает адрес доставки из другого города при смене города — подтягиваем актуальную корзину.
    await dispatch(getBasketById(basket.id));
  };

  const handleSubmit = (): void => {
    const normalizedAssignee = payload.assignee ? payload.assignee.split('_').slice(1).join(' ') : undefined;
    const normalizedPhone = payload.phone ? normalizePhone(payload.phone) : undefined;

    if (selectedStore && selectedStore.pickUpDate === EPickUpDate.ManagerWillConfirm) {
      payload.deliveryDate = null;
    }

    onSubmit({
      ...payload,
      assignee: normalizedAssignee,
      phone: normalizedPhone,
    });
  };

  return (
    <div>
      <Form
        form={form}
        requiredMark={renderRequiredMark}
        layout="vertical"
        scrollToFirstError={{ behavior: 'instant', block: 'end' }}
        onValuesChange={changeForm}
        onFinish={handleSubmit}
      >
        <div className="flex align-center gap-3">
          <Form.Item label="Контактное лицо" name="assignee" rules={[rules.required()]}>
            <UserAssigneeSelector />
          </Form.Item>

          <Form.Item
            className="flex-1"
            label="Телефон"
            name="phone"
            rules={[rules.required(), rules.pattern(PHONE_PATTERN_WITH_CODE, 'Неверный формат телефона')]}
          >
            <InputMask mask={PHONE_MASK}>
              {(inputProps: object) => (
                <Input
                  {...inputProps}
                  placeholder="+7 (___) ___-__-__"
                  suffix={
                    <Copy
                      className="icon-dark-grey cursor-pointer"
                      style={{
                        pointerEvents: isValidPhone ? 'auto' : 'none',
                        opacity: isValidPhone ? 1 : 0,
                      }}
                      onClick={copyPhone}
                    />
                  }
                />
              )}
            </InputMask>
          </Form.Item>
        </div>
      </Form>

      <div className="mb-6 ff-medium fs-m">
        {`Найдено ${availableStores.length} магазинов в городе`}
        <span
          className="ml-2 ff-medium fs-m color-bright-green text-decoration-underline cursor-pointer"
          onClick={openSelectCityModal}
        >
          {currentUser?.city?.name ?? ''}
        </span>
      </div>

      <div className="mb-5">
        <SearchInput auto minLength={DEFAULT_VALUE_0} placeholder="Выберите магазин самовывоза" onSearch={handleSearchStore} />
      </div>

      <Radio.Group
        className="pick-up-method__date-filter"
        optionType="button"
        options={dateOptions}
        value={pickUpDate}
        onChange={changePickUpDate}
      />

      <div className="flex direction-column" style={{ height: 356 }}>
        {!availableStores.length && (
          <div className="ff-regular fs-s color-dark-grey">
            {basketStores.length ? 'Магазин с таким адресом не найден.' : 'Список магазинов пуст'}
          </div>
        )}

        <div className="flex-1 overflow-y-scroll scrollbar-none">
          {availableStores.map((item) => {
            const selected = item.id === storeId;
            const schedules = formatBasketStoreSchedules(item.schedules);

            return (
              <div key={item.id}>
                <div
                  className={`p-3 flex align-center cursor-pointer pick-up-method__option ${selected ? 'pick-up-method__option-active' : ''}`}
                  onClick={() => changeStore(item.id)}
                >
                  {selected && <Tick className="mr-4 icon-bright-green" />}

                  <div className="flex-1">
                    <div className="mb-2 ff-regular fs-s" style={{ lineHeight: '20px' }}>
                      {item.address}
                    </div>

                    <div className="flex align-start justify-between gap-9">
                      <div className="flex align-start gap-3">
                        <History className="icon-dark-grey" />

                        <div className="flex align-center wrap">
                          {schedules.length ? (
                            schedules.map((item, index) => {
                              return (
                                <span
                                  key={index}
                                  className="mr-2 ff-regular fs-xs color-dark-grey text-nowrap basket-delivery-options-modal__pickup-option-schedule-item"
                                  style={{ lineHeight: '20px' }}
                                >
                                  {item}
                                  {index < schedules.length - 1 && ','}
                                </span>
                              );
                            })
                          ) : (
                            <span className="ff-regular fs-xs color-dark-grey" style={{ lineHeight: '20px' }}>
                              График работы не указан
                            </span>
                          )}
                        </div>
                      </div>

                      <span
                        className={`ff-regular fs-xs text-nowrap ${item.pickUpDate === EPickUpDate.Today ? 'color-bright-green' : 'color-dark-grey'}`}
                        style={{ lineHeight: '20px' }}
                      >
                        {translatePickUpDate(item.pickUpDate)}
                      </span>
                    </div>
                  </div>
                </div>

                <Divider marginY={8} />
              </div>
            );
          })}
        </div>

        {!!storeId && (
          <div className="mt-6">
            <Button className="button-lg primary w-full" style={{ minHeight: 52 }} onClick={form.submit}>
              Заберу заказ отсюда
            </Button>
          </div>
        )}
      </div>

      <SelectCityModal {...selectCityModalProps} onSubmit={handleUserCityChange} />
    </div>
  );
};
